/**
 * @fileoverview Quick Notes Component
 *
 * A floating, resizable notes widget that persists to localStorage.
 * Accessible via keyboard shortcut (Ctrl + Shift + N on all platforms).
 *
 * Features:
 * - Resizable interface
 * - Draggable positioning
 * - Auto-save to localStorage
 * - Rich text area with timestamp
 * - Minimize/maximize functionality
 * - Always on top when open
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiX, FiMinus, FiMaximize2, FiMinimize2, FiFileText, FiGrid } from 'react-icons/fi';
import '../styles/QuickNotes.css';

const QuickNotes = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [activeTab, setActiveTab] = useState('notes'); // 'notes' or 'calculator'
    const [notes, setNotes] = useState('');
    const [calcDisplay, setCalcDisplay] = useState('0');
    const [calcPrevValue, setCalcPrevValue] = useState(null);
    const [calcOperation, setCalcOperation] = useState(null);
    const [calcNewNumber, setCalcNewNumber] = useState(true);
    const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 100 });
    const [size, setSize] = useState({ width: 400, height: 500 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    const notesRef = useRef(null);
    const containerRef = useRef(null);

    // Load from localStorage on mount
    useEffect(() => {
        const savedNotes = localStorage.getItem('quickNotes');
        const savedPosition = localStorage.getItem('quickNotesPosition');
        const savedSize = localStorage.getItem('quickNotesSize');
        const savedVisibility = localStorage.getItem('quickNotesVisible');
        const savedTab = localStorage.getItem('quickNotesActiveTab');

        if (savedNotes) setNotes(savedNotes);
        if (savedPosition) setPosition(JSON.parse(savedPosition));
        if (savedSize) setSize(JSON.parse(savedSize));
        if (savedVisibility === 'true') setIsVisible(true);
        if (savedTab) setActiveTab(savedTab);
    }, []);

    // Save notes to localStorage
    useEffect(() => {
        if (notes !== null) {
            localStorage.setItem('quickNotes', notes);
        }
    }, [notes]);

    // Save position to localStorage
    useEffect(() => {
        localStorage.setItem('quickNotesPosition', JSON.stringify(position));
    }, [position]);

    // Save size to localStorage
    useEffect(() => {
        localStorage.setItem('quickNotesSize', JSON.stringify(size));
    }, [size]);

    // Save visibility to localStorage
    useEffect(() => {
        localStorage.setItem('quickNotesVisible', isVisible.toString());
    }, [isVisible]);

    // Save active tab to localStorage
    useEffect(() => {
        localStorage.setItem('quickNotesActiveTab', activeTab);
    }, [activeTab]);

    // Keyboard shortcut: Ctrl + Shift + N (all platforms)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'N') {
                e.preventDefault();
                setIsVisible(prev => !prev);
                if (isMinimized) setIsMinimized(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isMinimized]);

    // Handle dragging
    const handleMouseDown = useCallback((e) => {
        if (e.target.closest('.qn-resize-handle') || e.target.closest('.qn-close') ||
            e.target.closest('.qn-minimize') || e.target.closest('textarea')) {
            return;
        }

        setIsDragging(true);
        const rect = containerRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            const newX = Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.x));
            const newY = Math.max(0, Math.min(window.innerHeight - 50, e.clientY - dragOffset.y));
            setPosition({ x: newX, y: newY });
        } else if (isResizing) {
            const deltaX = e.clientX - resizeStart.x;
            const deltaY = e.clientY - resizeStart.y;

            const newWidth = Math.max(300, Math.min(800, resizeStart.width + deltaX));
            const newHeight = Math.max(200, Math.min(800, resizeStart.height + deltaY));

            setSize({ width: newWidth, height: newHeight });
        }
    }, [isDragging, isResizing, dragOffset, size, resizeStart]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

    // Handle resize
    const handleResizeStart = (e) => {
        e.stopPropagation();
        setIsResizing(true);
        setResizeStart({
            x: e.clientX,
            y: e.clientY,
            width: size.width,
            height: size.height
        });
    };

    // Insert timestamp
    const insertTimestamp = () => {
        const timestamp = new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        const textarea = notesRef.current;
        const cursorPos = textarea.selectionStart;
        const textBefore = notes.substring(0, cursorPos);
        const textAfter = notes.substring(cursorPos);
        const newText = `${textBefore}\n[${timestamp}]\n${textAfter}`;
        setNotes(newText);

        // Set cursor position after timestamp
        setTimeout(() => {
            const newCursorPos = cursorPos + timestamp.length + 3;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
            textarea.focus();
        }, 0);
    };

    // Clear all notes
    const handleClear = () => {
        setShowClearConfirm(true);
    };

    const confirmClear = () => {
        setNotes('');
        setShowClearConfirm(false);
    };

    const cancelClear = () => {
        setShowClearConfirm(false);
    };

    // Calculator functions
    const handleCalcNumber = (num) => {
        if (calcNewNumber) {
            setCalcDisplay(num.toString());
            setCalcNewNumber(false);
        } else {
            setCalcDisplay(calcDisplay === '0' ? num.toString() : calcDisplay + num);
        }
    };

    const handleCalcDecimal = () => {
        if (calcNewNumber) {
            setCalcDisplay('0.');
            setCalcNewNumber(false);
        } else if (!calcDisplay.includes('.')) {
            setCalcDisplay(calcDisplay + '.');
        }
    };

    const handleCalcOperation = (op) => {
        const currentValue = parseFloat(calcDisplay);

        if (calcPrevValue === null) {
            setCalcPrevValue(currentValue);
        } else if (calcOperation) {
            const result = performCalculation();
            setCalcDisplay(result.toString());
            setCalcPrevValue(result);
        }

        setCalcOperation(op);
        setCalcNewNumber(true);
    };

    const performCalculation = () => {
        const prev = calcPrevValue;
        const current = parseFloat(calcDisplay);

        switch (calcOperation) {
            case '+':
                return prev + current;
            case '-':
                return prev - current;
            case '*':
                return prev * current;
            case '/':
                return current !== 0 ? prev / current : 0;
            case '%':
                return prev % current;
            default:
                return current;
        }
    };

    const handleCalcEquals = () => {
        if (calcOperation && calcPrevValue !== null) {
            const result = performCalculation();
            setCalcDisplay(result.toString());
            setCalcPrevValue(null);
            setCalcOperation(null);
            setCalcNewNumber(true);
        }
    };

    const handleCalcClear = () => {
        setCalcDisplay('0');
        setCalcPrevValue(null);
        setCalcOperation(null);
        setCalcNewNumber(true);
    };

    const handleCalcBackspace = () => {
        if (calcDisplay.length > 1) {
            setCalcDisplay(calcDisplay.slice(0, -1));
        } else {
            setCalcDisplay('0');
            setCalcNewNumber(true);
        }
    };

    if (!isVisible) return null;

    return (
        <div
            ref={containerRef}
            className={`qn-container ${isMinimized ? 'qn-minimized' : ''} ${isDragging ? 'qn-dragging' : ''}`}
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: isMinimized ? '250px' : `${size.width}px`,
                height: isMinimized ? 'auto' : `${size.height}px`
            }}
        >
            {/* Header */}
            <div
                className="qn-header"
                onMouseDown={handleMouseDown}
            >
                <div className="qn-title">
                    <span className="qn-icon">{activeTab === 'notes' ? '📝' : '🔢'}</span>
                    {activeTab === 'notes' ? 'Quick Notes' : 'Calculator'}
                    <span className="qn-shortcut">(Ctrl+Shift+N)</span>
                </div>
                <div className="qn-actions">
                    <button
                        className="qn-minimize"
                        onClick={() => setIsMinimized(!isMinimized)}
                        title={isMinimized ? 'Maximize' : 'Minimize'}
                    >
                        {isMinimized ? <FiMaximize2 /> : <FiMinus />}
                    </button>
                    <button
                        className="qn-close"
                        onClick={() => setIsVisible(false)}
                        title="Close (Ctrl+Shift+N to reopen)"
                    >
                        <FiX />
                    </button>
                </div>
            </div>

            {/* Tabs */}
            {!isMinimized && (
                <div className="qn-tabs">
                    <button
                        className={`qn-tab ${activeTab === 'notes' ? 'active' : ''}`}
                        onClick={() => setActiveTab('notes')}
                    >
                        <FiFileText />
                        Notes
                    </button>
                    <button
                        className={`qn-tab ${activeTab === 'calculator' ? 'active' : ''}`}
                        onClick={() => setActiveTab('calculator')}
                    >
                        <FiGrid />
                        Calculator
                    </button>
                </div>
            )}

            {/* Content */}
            {!isMinimized && (
                <>
                    {activeTab === 'notes' ? (
                        <>
                            <div className="qn-toolbar">
                                <button
                                    className="qn-toolbar-btn"
                                    onClick={insertTimestamp}
                                    title="Insert timestamp"
                                >
                                    🕐 Add Timestamp
                                </button>
                                <button
                                    className="qn-toolbar-btn qn-clear"
                                    onClick={handleClear}
                                    title="Clear all notes"
                                >
                                    🗑️ Clear
                                </button>
                                <div className="qn-char-count">
                                    {notes.length} characters
                                </div>
                            </div>

                            <textarea
                                ref={notesRef}
                                className="qn-textarea"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Type your notes here...&#10;&#10;• Use Ctrl+Shift+N to toggle this window&#10;• Click 'Add Timestamp' to insert date/time&#10;• Notes are automatically saved&#10;• Drag the header to move&#10;• Drag bottom-right corner to resize"
                                spellCheck="true"
                            />
                        </>
                    ) : (
                        <div className="qn-calculator">
                            <div className="qn-calc-display">
                                <div className="qn-calc-operation">
                                    {calcPrevValue !== null && `${calcPrevValue} ${calcOperation || ''}`}
                                </div>
                                <div className="qn-calc-value">{calcDisplay}</div>
                            </div>

                            <div className="qn-calc-buttons">
                                <button className="qn-calc-btn qn-calc-fn" onClick={handleCalcClear}>C</button>
                                <button className="qn-calc-btn qn-calc-fn" onClick={handleCalcBackspace}>←</button>
                                <button className="qn-calc-btn qn-calc-fn" onClick={() => handleCalcOperation('%')}>%</button>
                                <button className="qn-calc-btn qn-calc-op" onClick={() => handleCalcOperation('/')}>÷</button>

                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(7)}>7</button>
                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(8)}>8</button>
                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(9)}>9</button>
                                <button className="qn-calc-btn qn-calc-op" onClick={() => handleCalcOperation('*')}>×</button>

                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(4)}>4</button>
                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(5)}>5</button>
                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(6)}>6</button>
                                <button className="qn-calc-btn qn-calc-op" onClick={() => handleCalcOperation('-')}>−</button>

                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(1)}>1</button>
                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(2)}>2</button>
                                <button className="qn-calc-btn" onClick={() => handleCalcNumber(3)}>3</button>
                                <button className="qn-calc-btn qn-calc-op" onClick={() => handleCalcOperation('+')}>+</button>

                                <button className="qn-calc-btn qn-calc-zero" onClick={() => handleCalcNumber(0)}>0</button>
                                <button className="qn-calc-btn" onClick={handleCalcDecimal}>.</button>
                                <button className="qn-calc-btn qn-calc-eq" onClick={handleCalcEquals}>=</button>
                            </div>
                        </div>
                    )}

                    {/* Resize handle */}
                    <div
                        className="qn-resize-handle"
                        onMouseDown={handleResizeStart}
                        title="Drag to resize"
                    >
                        <FiMinimize2 />
                    </div>
                </>
            )}

            {/* Clear Confirmation Modal */}
            {showClearConfirm && (
                <div className="qn-modal-overlay" onClick={cancelClear}>
                    <div className="qn-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="qn-modal-header">
                            <h3>Clear All Notes?</h3>
                        </div>
                        <div className="qn-modal-body">
                            <p>Are you sure you want to clear all notes? This action cannot be undone.</p>
                            <div className="qn-modal-stats">
                                <span className="qn-modal-icon">📝</span>
                                <span>{notes.length} characters will be deleted</span>
                            </div>
                        </div>
                        <div className="qn-modal-actions">
                            <button className="qn-modal-btn qn-modal-cancel" onClick={cancelClear}>
                                Cancel
                            </button>
                            <button className="qn-modal-btn qn-modal-confirm" onClick={confirmClear}>
                                Clear Notes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickNotes;
